import React from 'react';
import { Activity } from '../../../types/Activity';
import { usePermissions } from '../../../hooks/usePermissions';
import { Table, Badge, SortableTh, RowActions, ACTIONS_COL_WIDTH } from '@/components/ui';

interface ActivityTableProps {
  activities: Activity[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  onSort: (field: string) => void;
  onEditActivity: (activity: Activity) => void;
  onDeleteActivity: (id: number) => void;
}

const ActivityTable: React.FC<ActivityTableProps> = ({
  activities,
  sortField,
  sortDirection,
  onSort,
  onEditActivity,
  onDeleteActivity
}) => {
  const { hasPermission } = usePermissions();

  // Sort is server-side: the parent owns sortField/sortDirection and toggles direction,
  // so the shared SortableTh's computed direction is discarded and only the key is forwarded.
  const handleSort = (field: string) => onSort(field);

  return (
    <div className="tw-overflow-x-auto">
      <Table striped hover>
        <thead>
          <tr>
            <SortableTh label="Activity Number" sortKey="activityNumber" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Name" sortKey="activityName" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Unit of Measure" sortKey="unitOfMeasure" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            {/* Costing-derived columns are not server-sortable (to-many relation). */}
            <SortableTh label="Scheduled Rate" title="Sorting not available for this column" />
            <SortableTh label="Service Area" title="Sorting not available for this column" />
            <SortableTh label="Contract Year" title="Sorting not available for this column" />
            <SortableTh label="Classification" sortKey="classification" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Location Code" sortKey="locationCode" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <SortableTh label="Threshold" sortKey="threshold" activeKey={sortField} direction={sortDirection} onSort={handleSort} />
            <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {activities.map((activity) => (
            <tr key={activity.id}>
              <td>{activity.activityNumber}</td>
              <td>{activity.activityName}</td>
              <td>{activity.unitOfMeasure}</td>
              <td>${activity.scheduled2Rate != null ? activity.scheduled2Rate.toFixed(2) : '0.00'}</td>
              <td>{activity.serviceArea?.name}</td>
              <td>{activity.contractYear}</td>
              <td>
                <Badge bg={activity.classification === 'routine' ? 'primary' : 'success'}>
                  {activity.classification}
                </Badge>
              </td>
              <td>{activity.locationCode || '-'}</td>
              <td>{activity.threshold != null ? activity.threshold : '-'}</td>
              <td>
                <RowActions
                  actions={[
                    { kind: 'edit', label: 'Edit Activity', onClick: () => onEditActivity(activity) },
                    {
                      kind: 'delete',
                      label: 'Delete Activity',
                      onClick: () => onDeleteActivity(activity.id),
                      hidden: !hasPermission('activity:delete'),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ActivityTable;
